/**
 * SkillDelivery Platform
 * RoadmapStages — three 30/60/90 cards; first highlighted in brand colour.
 * Reusable by Phase-2 Your Workspace too.
 */
import React from 'react';

/**
 * @param {object} props
 * @param {Array<{when:string,title:string,bullets:string[]}>} props.steps
 * @param {string} [props.brandColor='#0891b2']
 */
export default function RoadmapStages({ steps, brandColor }) {
  // Release 12 Stage 2 -- the platform engine returns 3..6 steps per
  // display projection, so no longer capped at 3.
  const safeSteps = Array.isArray(steps) ? steps.slice(0, 6) : [];
  if (safeSteps.length === 0) return null;
  const brand = brandColor && /^#[0-9A-Fa-f]{6}$/.test(brandColor) ? brandColor : '#0891b2';
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
      gap: 16
    }}>
      {safeSteps.map((s, i) => (
        <Stage key={i} step={s} highlighted={i === 0} brandColor={brand} />
      ))}
    </div>
  );
}

function Stage({ step, highlighted, brandColor }) {
  const bg = highlighted
    ? `linear-gradient(135deg, ${brandColor}, ${_darken(brandColor, 0.2)})`
    : 'white';
  const text = highlighted ? '#ffffff' : '#0f172a';
  const sub = highlighted ? 'rgba(255,255,255,0.85)' : '#64748b';
  const border = highlighted ? 'transparent' : '#e2e8f0';
  const bulletColor = highlighted ? 'rgba(255,255,255,0.95)' : '#334155';
  return (
    <div style={{
      padding: 20,
      borderRadius: 12,
      background: bg,
      color: text,
      border: `1px solid ${border}`,
      boxShadow: highlighted ? '0 6px 18px rgba(8,145,178,0.25)' : '0 1px 2px rgba(15,23,42,0.05)'
    }}>
      {step.when && String(step.when).trim() !== '' ? (
        <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: sub, marginBottom: 6 }}>
          {step.when}
        </div>
      ) : null}
      <h3 style={{ margin: '0 0 10px', fontSize: 16, color: text }}>{step.title}</h3>
      <ul style={{ paddingLeft: 20, margin: 0, color: bulletColor, fontSize: 14, lineHeight: 1.5 }}>
        {(step.bullets || []).map((b, j) => <li key={j} style={{ marginBottom: 6 }}>{b}</li>)}
      </ul>
      {step.how_we_help && String(step.how_we_help).trim() !== '' ? (
        <div style={{
          marginTop: 12, paddingTop: 10, borderTop: `1px solid ${highlighted ? 'rgba(255,255,255,0.25)' : '#e2e8f0'}`,
          fontSize: 13, color: highlighted ? 'rgba(255,255,255,0.9)' : '#0e7490',
          fontStyle: 'italic'
        }}>
          <strong style={{ fontStyle: 'normal' }}>How SkillDelivery helps:</strong>{' '}{step.how_we_help}
        </div>
      ) : null}
      {/* R13 Step 1 fix A2 -- "Why this step" is the LAST line, after
          bullets and after how_we_help. Reader finishes with the purpose
          of the step before moving on. Scrubbed server-side; dropped
          silently when missing. */}
      {step.why && String(step.why).trim() !== '' ? (
        <div style={{
          marginTop: 10, fontSize: 13,
          color: highlighted ? 'rgba(255,255,255,0.95)' : '#64748b',
          fontStyle: 'italic'
        }}>
          <strong style={{ fontStyle: 'normal', color: highlighted ? 'rgba(255,255,255,0.95)' : '#334155' }}>Why this step:</strong>{' '}{step.why}
        </div>
      ) : null}
    </div>
  );
}

function _darken(hex, amt) {
  try {
    const r = Math.max(0, Math.round(parseInt(hex.slice(1, 3), 16) * (1 - amt)));
    const g = Math.max(0, Math.round(parseInt(hex.slice(3, 5), 16) * (1 - amt)));
    const b = Math.max(0, Math.round(parseInt(hex.slice(5, 7), 16) * (1 - amt)));
    return '#' + [r, g, b].map((n) => n.toString(16).padStart(2, '0')).join('');
  } catch (_) {
    return hex;
  }
}
